import { Box, Typography } from "@mui/material";
import { useIntl } from "react-intl";
import Messages from "../../../../../localization/Messages";
import ChartComponent, { MultiPieChartComponent } from "../../ChartComponent/ChartComponent";
import { IDock } from "../../../../../models/dock";
import { StatsBerthingsPerDock } from "../../../../../models/stats";
import Tooltip from "@mui/material/Tooltip";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

type DocksAnalyticsProps = {
    docks: IDock[];
    statsBerthingsPerDock: StatsBerthingsPerDock[];
};

export const DocksAnalytics = ({ docks, statsBerthingsPerDock }: DocksAnalyticsProps) => {
    const intl = useIntl();
    const dataAvailable = statsBerthingsPerDock.length > 0;
    const totals = dataAvailable
        ? statsBerthingsPerDock.reduce(
              (acc, dock) => {
                  acc.totalBerthings += dock.gt110 + dock.le110;
                  acc.totalGt110 += dock.gt110;
                  acc.totalLe110 += dock.le110;
                  return acc;
              },
              {
                  totalBerthings: 0,
                  totalGt110: 0,
                  totalLe110: 0,
              },
          )
        : {
              totalBerthings: 0,
              totalGt110: 0,
              totalLe110: 0,
          };

    const { totalBerthings, totalGt110, totalLe110 } = totals;

    return (
        <Box className="docks-analytics-container shadow" my={2} mx={2.5} padding={2.5}>
            <Typography variant="h5">
                {intl.formatMessage(Messages.docks)}
                <Tooltip title={intl.formatMessage(Messages.onlyInvoicedConfirmedChangedTooltip)}>
                    <HelpOutlineIcon fontSize="small" color="action" />
                </Tooltip>
            </Typography>

            <Box display="flex" justifyContent={"center"} gap={1}>
                <Box width={"60%"}>
                    <ChartComponent
                        data={
                            dataAvailable
                                ? statsBerthingsPerDock.map(entry => {
                                      return {
                                          label:
                                              docks.find(dock => dock.id === entry.id)?.name || "",
                                          value: entry.gt110 + entry.le110,
                                      };
                                  })
                                : []
                        }
                        description={intl.formatMessage(Messages.numberOfBerthingsPerDock)}
                        outerStats={[]}
                        formatter={value =>
                            `${value} / ${((value / totalBerthings) * 100).toFixed(2)}%`
                        }
                    />
                </Box>

                <Box display="flex">
                    <MultiPieChartComponent
                        description={intl.formatMessage(Messages.numberOfVesselsPerDockByLength)}
                        chartText1={`${intl.formatMessage(Messages.numberOfVessels)} > 110m`}
                        chartText2={`${intl.formatMessage(Messages.numberOfVessels)} ≤ 110m`}
                        data1={
                            dataAvailable
                                ? statsBerthingsPerDock.map(entry => {
                                      return {
                                          label:
                                              docks.find(dock => dock.id === entry.id)?.name || "",
                                          value: entry.gt110,
                                      };
                                  })
                                : []
                        }
                        formatter1={value =>
                            `${value} / ${((value / totalGt110) * 100).toFixed(2)}%`
                        }
                        data2={
                            dataAvailable
                                ? statsBerthingsPerDock.map(entry => {
                                      return {
                                          label:
                                              docks.find(dock => dock.id === entry.id)?.name || "",
                                          value: entry.le110,
                                      };
                                  })
                                : []
                        }
                        formatter2={value =>
                            `${value} / ${((value / totalLe110) * 100).toFixed(2)}%`
                        }
                    />
                </Box>
            </Box>
        </Box>
    );
};
